import { mdiClipboardText, mdiTag, mdiArchiveClock, mdiEmail } from "@mdi/js"
import { get, writable } from "svelte/store"
import { _ } from "svelte-i18n"
import { setUser } from "./sentry"
import type { ComponentType } from "svelte"

// Scrolling

export const pageScroll = writable({
    scrollY: 0,
    innerHeight: 0,
    outerHeight: 0,
})

const initLoc = {
    path: (typeof window !== "undefined" && window.location?.pathname) || "/",
    search: (typeof window !== "undefined" && window.location?.search) || "",
    hash: (typeof window !== "undefined" && window.location?.hash) || "",
    push: false,
    pop: false,
}

export const location = writable(initLoc)

///// AUTH
export const login = writable<Login | null>(null)
export const showPrices = writable(false)
login.subscribe(($login) => {
    const roles = $login?.account?.roles
    if (roles && (roles.includes("A") || roles.includes("R") || roles.includes("C"))) {
        showPrices.set(true)
    } else {
        showPrices.set(false)
    }

    // track user to sentry
    setUser({
        username: $login?.account?.ident && $login.account.ident.dealerNumber + "-" + $login.account.ident.personNumber,
        email: $login?.account?.email,
    })
})

///// Notifications
export const notifications = writable<NotificationEntry[]>([])
export function newNotification(n: NotificationEntry): NotificationEntry {
    notifications.update((_n) => {
        _n.push(n)
        let timeout = n.timeout
        if (!timeout) timeout = 3500 // default
        if (timeout > 0) {
            setTimeout(() => {
                removeNotification(n)
            }, timeout)
        }
        return _n
    })

    return n
}
export function removeNotification(n: NotificationEntry) {
    notifications.update((_n) => {
        return _n.filter((v) => {
            return v !== n
        })
    })
}

location.subscribe((l) => {
    notifications.update((_n) => {
        return _n.filter((v) => {
            return !v.removeOnNewLocation
        })
    })
})

///// Overlay
export const overlayTabs = writable<OverlayTab[] | null>(null)

// Operation, die nach dem nächsten Locations-Wechsel als Overlay geöffnet werden soll
// (z.B. Kurzlink /de/op/:number -> navigateTo /operations?type=X -> Liste öffnet Overlay)
export const pendingOverlayOperation = writable<Operation | OperationOut | null>(null)

export function openOverlay(
    op: Operation | OperationOut,
    what: "details" | "inquiry" | "protocol" | "email" = "details",
    props?: any
) {
    // dont open for yellow status
    const $login = get(login)

    if (
        !$login?.account?.roles?.includes("A") &&
        what == "details" &&
        // @ts-ignore
        (op.statusSummary?.created < 100 || (op.type == 2 && op.statusSummary?.confirmed < 100)) &&
        op.collectionName == "operation"
    ) {
        newNotification({
            class: "warning",
            removeOnNewLocation: true,
            html: get(_)("general.incompleteInfo"),
        })
        return
    }

    let icon
    if (what === "inquiry") {
    } else if (what === "protocol") icon = mdiArchiveClock
    else if (what === "email") icon = mdiEmail
    else if (op.type === 1) icon = mdiTag
    else icon = mdiClipboardText
    const OperationDetails = require("./components/routes/operations/OperationDetails.svelte")
    const OperationInquiry = require("./components/routes/operations/OperationInquiry.svelte")
    const OperationProtocol = require("./components/routes/operations/OperationProtocol.svelte")
    const OperationEmail = require("./components/routes/operations/OperationEmail.svelte")
    let component: ComponentType
    if (what === "inquiry") component = OperationInquiry.default
    else if (what === "protocol") component = OperationProtocol.default
    else if (what === "email") component = OperationEmail.default
    else component = OperationDetails.default

    let offerTabTitle_Type1 = get(_)("general.offer")
    let title
    if (what === "inquiry") {
        title = get(_)("operations.inquiry", { values: { opNumber: op.number } })
    } else if (what === "protocol") {
        title = get(_)("operations.protocol", { values: { opNumber: op.number } })
    } else if (what === "email") {
        title = get(_)("operations.email", { values: { opNumber: op.number } })
    } else if (op.type === 1) {
        title = get(_)("operations.offerType1", { values: { offerTabTitle_Type1, opNumber: op.number } })
    } else if (op.type === 2 && op.collectionName === "operation-out") {
        title = get(_)("operations.orderConfirmation", { values: { opNumber: op.number } })
    } else {
        title = get(_)("operations.order", { values: { opNumber: op.number } })
    }

    const tab = {
        icon: icon,
        title: title,
        subtitle:
            (op.commission || "") +
            (op.commission && op.constructionProject ? ", " : "") +
            (op.constructionProject || ""),
        component,
        props: {
            id: op.id,
            type: op.type,
            collectionName: op.collectionName,
            ...props,
        },
        active: true,
    }

    // @ts-ignore
    if (op["activePosition"]) {
        // @ts-ignore
        tab.props.activePosition = op["activePosition"]
    }

    const _oTabs = get(overlayTabs)
    if (_oTabs) {
        let alreadyExisting = false
        _oTabs.forEach((t) => {
            if (t.props?.id == op.id && t.component == component) {
                t.active = true
                t.props = tab.props
                alreadyExisting = true
            } else {
                t.active = false
            }
        })

        if (alreadyExisting) {
            overlayTabs.set([..._oTabs])
        } else {
            overlayTabs.set([..._oTabs, tab])
        }
    } else {
        overlayTabs.set([tab])
    }
}

///////////// content floater

export const contentFloaterPath = writable<string | null>(null)

////////////// shared accessToken

export const accessToken = writable<string | null>(null)

///////////// bookmarks

export const bookmarks = writable<BookmarksMap>({
    operation: {},
    "operation-out": {},
    personNumbers: [],
})

export const followUpMap = writable<FollowUpMap>({
    operation: {},
    "operation-out": {},
    personNumbers: [],
})
///////////// carts

export const carts = writable<Cart[] | null>(null)
export const eventQueue = writable<string[]>([])
/**
 * Zustand des gerade laufenden Bestellvorgangs:
 *  - `null`  : kein Bestellvorgang
 *  - `false` : Bestellung wurde ausgelöst
 *  - Objekt  : laufende Bestellung; trägt Name und Quell-Warenkorb-ID
 */
export type OrderCartMarker = { name: string; cartID: string }
export const orderCart = writable<OrderCartMarker | false | null>(null)
export const formType = writable<{ [key: string]: "person" | "company" }>({})
export const formFoucs = writable<string>("")
export const operationHashBeginning = writable<string | null>(null)
export const openModal = writable<boolean>(false)
export const rerender = writable<boolean>(false)
export const filesStore = writable<{ [operationID: string]: Attachement[] }>({})
export const appliedPricegroups = writable<{
    [operationId: string]: [string, string]
}>({})

///////////// last activity

export const logoutAfterInactivitySeconds = 60 * 60 * 2 // 2 hours
export const logoutInSeconds = writable<number | null>(logoutAfterInactivitySeconds)
export const lastActivity = writable<Date>(new Date())
// update on every user interaction
if (typeof document !== "undefined") {
    document.addEventListener("click", () => {
        lastActivity.set(new Date())
        logoutInSeconds.set(logoutAfterInactivitySeconds)
    })
    document.addEventListener("keydown", () => {
        lastActivity.set(new Date())
        logoutInSeconds.set(logoutAfterInactivitySeconds)
    })
}

export const accessTokenExpireSeconds = writable<number | null>(null)
login.subscribe(($login) => {
    if (!$login) {
        accessTokenExpireSeconds.set(null)
    }
})
